import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { supabase } from "@/integrations/supabase/client";
import { APP_VERSION } from "@/lib/appVersion";
import {
  DETAILS_MAX_CHARS,
  safeSnapshot,
  type ReportReason,
  type ReportSurface,
  type SnapshotKind,
} from "@/lib/report/surfaces";

/**
 * ReportedContentContext — parte C do fix da politica de conteudo de IA
 * (Google, 18/08/2026): o conteudo denunciado some para AQUELE usuario e
 * CONTINUA sumido depois de fechar e reabrir o app.
 *
 * A decisao que sustenta tudo: a lista de ocultacao E a propria tabela
 * content_reports. Nao existe um segundo mecanismo — nem localStorage, nem
 * flag em outra tabela. Consequencias, todas exigidas pela politica:
 *
 *   - sobrevive a recarga, porque o estado vive no Postgres e nao na memoria;
 *   - e POR USUARIO por construcao, porque o RLS escopa o SELECT em
 *     auth.uid() — uma denuncia jamais esconde conteudo de terceiros, o que
 *     seria moderacao automatica sem revisao humana;
 *   - nao pode divergir do que a moderacao ve, porque e a mesma linha.
 *
 * Um localStorage seria mais rapido e estaria errado das tres maneiras: some
 * ao limpar dados, nao acompanha o usuario para outro aparelho e cria um
 * segundo lugar onde "denunciado" pode ser verdade sem que a linha exista.
 *
 * Carregamento: um SELECT no boot (ids apenas, sem snapshot — a lista pode ter
 * centenas de linhas e o snapshot e KBs cada). Ate as linhas chegarem o Set
 * esta vazio, entao NADA e escondido: preferir mostrar por ~200ms a esconder o
 * app inteiro por engano. Quando chegam, o filtro entra e o item some.
 *
 * Nao ha flag `ready` publica: ela existiu, nenhuma tela leu, e uma flag que
 * ninguem consome so cria a duvida de quem le o codigo depois ("devo esperar
 * isso antes de renderizar?"). Se algum dia uma superficie precisar evitar o
 * flash, ela volta COM o consumidor no mesmo commit.
 */

type ReportKey = string;

/**
 * content_reports ainda nao existe em src/integrations/supabase/types.ts (o
 * arquivo e gerado e regenera-lo aqui traria um diff de milhares de linhas sem
 * relacao com este PR). Em vez do `(supabase as any)` que o repo usa nesses
 * casos, o escape fica TIPADO no minimo que este arquivo realmente chama: um
 * `any` solto esconderia um erro de nome de coluna ate o INSERT rodar — que e
 * o pior momento possivel, porque e a hora da denuncia.
 */
interface ContentReportRow {
  user_id: string;
  surface: string;
  content_id: string;
  reason: string;
  details: string | null;
  content_snapshot: string;
  snapshot_kind: SnapshotKind;
  context: Record<string, unknown>;
  app_version: string | null;
  platform: string | null;
  language: string | null;
}

interface UntypedTable {
  select: (cols: string) => {
    eq: (col: string, val: string) => PromiseLike<{
      data: Array<{ surface: string; content_id: string }> | null;
      error: { message?: string } | null;
    }>;
  };
  upsert: (
    row: ContentReportRow,
    opts: { onConflict: string; ignoreDuplicates: boolean },
  ) => PromiseLike<{ error: { message?: string } | null }>;
}

const reportsTable = (): UntypedTable =>
  (supabase as unknown as { from: (t: string) => UntypedTable }).from("content_reports");

function keyOf(surface: ReportSurface | string, contentId: string): ReportKey {
  return surface + " " + contentId;
}

export interface SubmitReportInput {
  surface: ReportSurface;
  contentId: string;
  reason: ReportReason;
  details?: string;
  /** O conteudo COMO ESTAVA na tela. Texto, storage path ou JSON serializado. */
  snapshot: string;
  snapshotKind?: SnapshotKind;
  /** Extras por superficie (session_id, modulo, formato). Nunca obrigatorio. */
  context?: Record<string, unknown>;
  language?: string;
}

interface ReportedContentContextType {
  isReported: (surface: ReportSurface | string, contentId: string | null | undefined) => boolean;
  /** Resolve quando a linha esta no banco. Rejeita quando o INSERT falhou. */
  submitReport: (input: SubmitReportInput) => Promise<void>;
}

const ReportedContentContext = createContext<ReportedContentContextType>({
  isReported: () => false,
  submitReport: async () => {
    throw new Error("ReportedContentProvider ausente");
  },
});

export const ReportedContentProvider = ({ children }: { children: React.ReactNode }) => {
  const [reported, setReported] = useState<Set<ReportKey>>(() => new Set());

  // Espelho sincrono do Set (CLAUDE.md secao 11): `submitReport` precisa saber
  // se a chave ja estava la ANTES de escrever, e ler isso de dentro de um
  // updater seria justamente o bug do updater impuro — que acerta na primeira
  // interacao depois da montagem e erra em todas as seguintes.
  const reportedRef = useRef<Set<ReportKey>>(new Set());

  const applySet = useCallback((next: Set<ReportKey>) => {
    reportedRef.current = next;
    setReported(next);
  }, []);

  useEffect(() => {
    let active = true;

    const load = async (): Promise<void> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!active) return;
        if (!user) {
          // Deslogado e estado normal, nao falha: nada a esconder.
          applySet(new Set());
          return;
        }

        const { data, error } = await reportsTable()
          .select("surface, content_id")
          .eq("user_id", user.id);

        if (!active) return;

        if (error) {
          // O fallback MENTE: renderiza conteudo que o usuario ja denunciou.
          // Secao 11 — este e `error`, nao `warn`.
          console.error("[ReportedContent] load falhou - filtro entra vazio", error);
          applySet(new Set());
          return;
        }

        const next = new Set<ReportKey>();
        for (const row of data ?? []) {
          next.add(keyOf(row.surface, row.content_id));
        }
        applySet(next);
      } catch (err) {
        if (!active) return;
        console.error("[ReportedContent] load lancou", err);
        applySet(new Set());
      }
    };

    void load();

    // Trocar de usuario no mesmo processo tem de trocar o filtro junto, senao o
    // segundo usuario herda as ocultacoes do primeiro.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN" || event === "SIGNED_OUT" || event === "USER_UPDATED") {
        void load();
      }
    });

    return () => {
      active = false;
      sub?.subscription?.unsubscribe();
    };
  }, [applySet]);

  const isReported = useCallback(
    (surface: ReportSurface | string, contentId: string | null | undefined): boolean => {
      if (!contentId) return false;
      return reported.has(keyOf(surface, contentId));
    },
    [reported],
  );

  const submitReport = useCallback(
    async (input: SubmitReportInput): Promise<void> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("not_authenticated");

      const k = keyOf(input.surface, input.contentId);
      const hadIt = reportedRef.current.has(k);

      // Otimista: o conteudo some no toque, nao depois do round-trip. Se o
      // INSERT falhar, desfaz — dizer "escondido" sem linha gravada seria a
      // mentira exata que a politica proibe.
      if (!hadIt) {
        const next = new Set(reportedRef.current);
        next.add(k);
        applySet(next);
      }

      const details = (input.details ?? "").trim().slice(0, DETAILS_MAX_CHARS);

      const { error } = await reportsTable()
        .upsert(
          {
            user_id: user.id,
            surface: input.surface,
            content_id: input.contentId,
            reason: input.reason,
            details: details.length > 0 ? details : null,
            content_snapshot: safeSnapshot(input.snapshot),
            snapshot_kind: input.snapshotKind ?? "text",
            context: input.context ?? {},
            app_version: APP_VERSION,
            platform: Capacitor.getPlatform(),
            language: input.language ?? null,
          },
          // Denunciar duas vezes e inocuo: a UNIQUE (user_id, surface,
          // content_id) absorve o segundo toque sem duplicar linha e sem erro.
          // ignoreDuplicates = ON CONFLICT DO NOTHING, que so precisa de
          // INSERT — e por isso que `authenticated` nao tem UPDATE na tabela.
          { onConflict: "user_id,surface,content_id", ignoreDuplicates: true },
        );

      if (error) {
        if (!hadIt) {
          const revert = new Set(reportedRef.current);
          revert.delete(k);
          applySet(revert);
        }
        console.error("[ReportedContent] INSERT falhou", error);
        throw error;
      }
    },
    [applySet],
  );

  return (
    <ReportedContentContext.Provider value={{ isReported, submitReport }}>
      {children}
    </ReportedContentContext.Provider>
  );
};

export const useReportedContent = () => useContext(ReportedContentContext);
